import { z } from "zod";

/**
 * Structured classification of a clinic call. Produced by the NotifyMD
 * extraction and by the voice agent (sent in the telephone-encounter API request), and
 * used to route the encounter to the right ECW staff inbox per tenant.
 */
export enum ClinicCallReason {
  ALLERGY_RELATED = "Allergy Related",
  APPOINTMENT_INQUIRY = "Appointment Inquiry",
  BILLING_QUESTION = "Billing Question",
  CANCEL_APPOINTMENT = "Cancel Appointment",
  CLINICAL_QUESTION = "Clinical Question",
  DME_INQUIRY = "DME Inquiry",
  INSURANCE_ELIGIBILITY = "Insurance Eligibility",
  MEDICATION_RELATED = "Medication Related",
  OTHER = "Other",
  PACEMAKER_DEVICE_ALERT = "Pacemaker Device Alert",
  PRESCRIPTION_REFILL = "Prescription Refill",
  REFERRAL_QUESTION = "Referral Question",
  RESCHEDULE_APPOINTMENT = "Reschedule Appointment",
  RESULTS_INQUIRY = "Results Inquiry",
  SCHEDULE_APPOINTMENT = "Schedule Appointment",
  SLEEP_APPOINTMENT = "Sleep Appointment",
  SLEEP_STUDY_INQUIRY = "Sleep Study Inquiry",
  WORKERS_COMPENSATION = "Workers Compensation",
}

export type CommonClinicCallReason = Exclude<
  ClinicCallReason,
  ClinicCallReason.PACEMAKER_DEVICE_ALERT | ClinicCallReason.PRESCRIPTION_REFILL | ClinicCallReason.WORKERS_COMPENSATION
>;

export function isCommonClinicCallReason(reason: ClinicCallReason): reason is CommonClinicCallReason {
  return (
    reason !== ClinicCallReason.PACEMAKER_DEVICE_ALERT &&
    reason !== ClinicCallReason.PRESCRIPTION_REFILL &&
    reason !== ClinicCallReason.WORKERS_COMPENSATION
  );
}

/** Clinic-call reasons supported by every tenant. Tenant-specific extensions must opt in explicitly. */
export const COMMON_CLINIC_CALL_REASONS = Object.values(ClinicCallReason).filter(isCommonClinicCallReason);

/** Preserve the pre-extension vocabulary for producers without tenant-specific routing. */
export const legacyClinicCallReasonSchema = z.enum([
  ...COMMON_CLINIC_CALL_REASONS,
  ClinicCallReason.PACEMAKER_DEVICE_ALERT,
]);

export const CLINIC_CALL_REASON_CLASSIFICATION_PROMPT = [
  "Choose the category from the caller's requested action, not from incidental topic words. When more than one category seems relevant, the explicit action the caller wants takes precedence.",
  'Use "Appointment Inquiry" when the caller asks whether an appointment is needed or has another appointment-related question, even when the appointment would be used to discuss test results.',
  'Use "DME Inquiry" for a physical device or supply, including CPAP pressure changes, machines, masks, supplies, pickup or return, and equipment troubleshooting.',
  'Use "Sleep Appointment" for scheduling or changing a sleep-study appointment. Use "Sleep Study Inquiry" for an order, preparation, status, or results of the sleep study itself, not for the CPAP device or other equipment.',
  'Use "Results Inquiry" when the caller wants to obtain or follow up on non-sleep test results themselves and is not asking whether an appointment is needed.',
].join(" ");
